/**
 * subscription-visibility.js
 *
 * Shows/hides elements marked `[data-hide-on-onetime]` based on the Recharge
 * "Subscription Widget V2" selection: visible when a subscription option is
 * selected, hidden on one-time. Used for the Refill plan and Gifts-with-purchase
 * blocks, which only apply to subscription purchases.
 *
 * NOTE: this uses a bespoke `data-hide-on-onetime` attribute rather than the
 * theme's native `data-subscription-only` — the theme renders that native
 * attribute on the buy-buttons/sticky-ATC blocks (even with value "false"), so
 * matching it by presence would wrongly hide Add to Cart on one-time.
 *
 * Each marked element carries `data-product-id` so the correct Recharge widget is
 * matched. Detection mirrors subscription-price.js: read the widget's reactive
 * `purchaseOption` / `sellingPlanId`, with a lightweight persistent poll as a
 * bulletproof backstop for the opaque third-party widget.
 *
 * Safe defaults: before the widget is ready, elements stay visible (never hide
 * permanently if Recharge fails to load) and are corrected on the first poll.
 * Idempotent — guarded so it initialises once even if included more than once.
 */
(function () {
  if (window.__subscriptionVisibilityInit) return;
  window.__subscriptionVisibilityInit = true;

  function widgetFor(productId) {
    return (
      (productId && document.querySelector(`recharge-subscription-widget[product-id="${productId}"]`)) ||
      document.querySelector('recharge-subscription-widget')
    );
  }

  function isSubscription(widget) {
    if (!widget) return null; // unknown yet
    const option = widget.purchaseOption;
    if (option === 'onetime') return false;
    if (typeof option === 'string' && option.length) return true;
    return Boolean(widget.sellingPlanId);
  }

  // Overrides from a custom <purchase-options> toggle (Quick View, where there is no widget).
  var overrides = {};

  function sync() {
    const els = document.querySelectorAll('[data-hide-on-onetime]');
    const retained = window.__purchaseOptionState || {};
    els.forEach((el) => {
      const pid = el.dataset.productId;
      // A custom <purchase-options> toggle (Quick View, no widget) wins: the retained global
      // covers the initial-load race, the overrides map covers live changes; otherwise read the
      // Recharge widget (unknown → keep visible; hide on one-time).
      let state;
      if (pid && retained[pid]) {
        state = retained[pid].isSubscription;
      } else if (pid && Object.prototype.hasOwnProperty.call(overrides, pid)) {
        state = overrides[pid];
      } else {
        state = isSubscription(widgetFor(pid));
      }
      el.hidden = state === false;
    });
  }

  // Always listen: Quick View injects marked blocks after load, so an empty page now
  // doesn't mean there is nothing to manage later (sync is a no-op when none exist).
  function init() {
    document.addEventListener('purchase-option:change', (e) => {
      overrides[String(e.detail.productId)] = e.detail.isSubscription;
      sync();
    });
    sync();
    setInterval(sync, 400);
    // React instantly to clicks inside the Recharge widget too.
    document.addEventListener(
      'click',
      (e) => {
        if (e.target.closest && e.target.closest('recharge-subscription-widget')) {
          setTimeout(sync, 0);
        }
      },
      true
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
